import React, { useState, useEffect, useCallback } from 'react';
import { keyframes } from '@emotion/react';
import styled from '@emotion/styled';
import { signInWithPopup } from 'firebase/auth';
import { auth, googleProvider, microsoftProvider, isAllowedEmail } from '../firebase';
import { useAuth } from '../contexts/AuthContext';
import { useNotification } from '../contexts/NotificationContext';
import { useTheme } from '../contexts/ThemeContext';
import { brandColors, gradients, canonical } from '../design-system/tokens';

// Chaney font for GUS text
const chaneyFont = `
  @font-face {
    font-family: 'Chaney';
    src: url('/fonts/chaney-regular-webfont.woff2') format('woff2'),
         url('/fonts/chaney-regular-webfont.woff') format('woff');
    font-weight: normal;
    font-style: normal;
    font-display: swap;
  }
`;

// Inject Chaney font into document head
if (typeof document !== 'undefined') {
  const styleId = 'chaney-font-face';
  if (!document.getElementById(styleId)) {
    const style = document.createElement('style');
    style.id = styleId;
    style.textContent = chaneyFont;
    document.head.appendChild(style);
  }
}

/**
 * Visual layer follows silacities-gus-v2/design.md -- the normative GUS design
 * system. The register is "modern-minimal, editorial. Enterprise analytics:
 * dense, quiet, data-first. Straight lines over soft ones. It is not a marketing
 * site and should never acquire the chrome of one."
 *
 * Concretely that means: radius 0 on every rectangle (6px survives on fields,
 * where roundness IS the affordance), no glass/backdrop blur, no ambient
 * animation, the Sila triplet only, and the teal accent kept under ~5% of the
 * viewport -- "a highlighter, not a fill".
 *
 * Theming is done with custom properties set once on the container rather than
 * threaded through props: this file uses @emotion/styled while ThemeContext
 * provides a styled-components ThemeProvider, so props.theme is NOT populated
 * here. var(--lp-*) is the only safe token channel.
 */

const spin = keyframes`
  to { transform: rotate(360deg); }
`;

interface SurfaceProps {
  $dark: boolean;
}

const LoginContainer = styled.div<SurfaceProps>`
  /* Sila triplet -- the only brand colours, identical in both themes. */
  --lp-blue: ${brandColors.silaBlue};
  --lp-teal: ${brandColors.silaTeal};
  --lp-green: ${brandColors.silaGreen};
  --lp-accent: var(--lp-teal);
  --lp-accent-solid: ${canonical.accentSolid};
  --lp-brand-gradient: ${gradients.brand};

  --lp-r-square: ${canonical.radius.square};
  --lp-r-field: ${canonical.radius.field};

  ${({ $dark }) =>
    $dark
      ? `
  --lp-bg: #0A0A0A;
  --lp-surface: #1A1A1A;
  --lp-surface-sunken: #121212;
  --lp-fg: #FFFFFF;
  --lp-fg-secondary: #D4D4D4;
  --lp-fg-muted: #A3A3A3;
  --lp-border: rgba(255, 255, 255, 0.14);
  --lp-border-strong: rgba(255, 255, 255, 0.24);
  --lp-danger: #FCA5A5;
  --lp-danger-bg: rgba(220, 38, 38, 0.14);
  /* On dark, elevation reads as lightness, not a shadow halo (design.md § Depth). */
  --lp-elevation: none;
`
      : `
  --lp-bg: #FAFAFA;
  --lp-surface: #FFFFFF;
  --lp-surface-sunken: #F5F5F5;
  --lp-fg: #000000;
  --lp-fg-secondary: #404040;
  --lp-fg-muted: #737373;
  --lp-border: #E5E5E5;
  --lp-border-strong: #D4D4D4;
  --lp-danger: #B91C1C;
  --lp-danger-bg: rgba(185, 28, 28, 0.07);
  --lp-elevation: 0 1px 2px rgba(0, 0, 0, 0.04), 0 8px 24px rgba(0, 0, 0, 0.06);
`}

  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 100vh;
  min-height: 100dvh;
  padding: 32px 16px 72px;
  box-sizing: border-box;
  position: relative;
  font-family: ${canonical.fontFamily.body};
  background: var(--lp-bg);
  color: var(--lp-fg);
`;

const LoginBox = styled.div`
  position: relative;
  width: 100%;
  max-width: 440px;
  box-sizing: border-box;
  padding: 40px;
  background: var(--lp-surface);
  border: 1px solid var(--lp-border);
  border-radius: var(--lp-r-square);
  box-shadow: var(--lp-elevation);

  /* The one editorial flourish: the Sila triplet as a 2px rule along the top
     edge. Carries the brand without flooding anything with accent. */
  &::before {
    content: '';
    position: absolute;
    inset: 0 0 auto 0;
    height: 2px;
    background: var(--lp-brand-gradient);
  }

  @media (max-width: 420px) {
    padding: 28px 20px;
  }
`;

const LogoContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  margin-bottom: 28px;
`;

const LogoImage = styled.img`
  height: 32px;
  width: auto;
  max-width: 100%;
`;

const SystemName = styled.div`
  font-family: 'Chaney', 'Arial Black', ${canonical.fontFamily.body};
  font-size: clamp(44px, 13vw, 60px);
  font-weight: normal;
  font-style: normal;
  line-height: 1;
  letter-spacing: 6px;
  padding-left: 6px; /* optical: letter-spacing pads the last glyph only */
  text-transform: uppercase;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;

  /* design.md allows a gradient background-clip on one or two words. GUS is one.
     Static, not animated -- the ambient shimmer was marketing chrome. */
  background: var(--lp-brand-gradient);
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
  color: var(--lp-teal); /* fallback where background-clip: text is unsupported */
`;

const AdminBadge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 10px;
  border: 1px solid var(--lp-border-strong);
  border-radius: var(--lp-r-square);
  font-family: ${canonical.fontFamily.mono};
  font-size: 10.5px;
  font-weight: 600;
  letter-spacing: 1.5px;
  text-transform: uppercase;
  color: var(--lp-fg-secondary);
  background: var(--lp-surface-sunken);
`;

const Title = styled.h1`
  margin: 0 0 6px 0;
  text-align: center;
  font-size: 19px;
  font-weight: 600;
  font-style: normal; /* headings are roman, never italic */
  line-height: 1.3;
  letter-spacing: -0.01em;
  color: var(--lp-fg);
  overflow-wrap: anywhere;
  min-width: 0;
`;

const Subtitle = styled.p`
  margin: 0 0 28px 0;
  text-align: center;
  font-size: 13.5px;
  line-height: 1.55;
  color: var(--lp-fg-muted);
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 14px;
`;

const InputGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const Label = styled.label`
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 1px;
  color: var(--lp-fg-secondary);

  /* Uppercase tracking is an LTR affordance (design.md § Typography). */
  &:dir(ltr) {
    text-transform: uppercase;
  }
`;

const Input = styled.input`
  width: 100%;
  box-sizing: border-box;
  padding: 11px 13px;
  font-size: 14px;
  font-family: inherit;
  color: var(--lp-fg);
  background: var(--lp-surface-sunken);
  border: 1px solid var(--lp-border-strong);
  border-radius: var(--lp-r-field);
  transition: border-color 140ms ease, background-color 140ms ease, box-shadow 140ms ease;

  &::placeholder {
    color: var(--lp-fg-muted);
  }

  &:hover:not(:disabled):not(:focus) {
    border-color: var(--lp-fg-muted);
  }

  &:focus-visible,
  &:focus {
    outline: none;
    background: var(--lp-surface);
    border-color: var(--lp-accent);
    box-shadow: 0 0 0 2px var(--lp-accent);
  }

  &:disabled {
    opacity: 0.55;
    cursor: not-allowed;
  }

  &[aria-invalid='true'] {
    border-color: var(--lp-danger);
  }
`;

/* Shared button geometry. Square, per design.md -- a button is a rectangle that
   happened to be rounded, and squaring it costs nothing. */
const buttonBase = `
  width: 100%;
  box-sizing: border-box;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  padding: 12px 20px;
  font-size: 14px;
  font-weight: 600;
  font-family: inherit;
  line-height: 1.2;
  white-space: nowrap; /* a clickable label must never wrap to two lines */
  border-radius: var(--lp-r-square);
  cursor: pointer;
  transition: background-color 140ms ease, border-color 140ms ease, color 140ms ease;

  &:focus-visible {
    outline: 2px solid var(--lp-accent);
    outline-offset: 2px;
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.55;
  }
`;

const Button = styled.button`
  ${buttonBase}
  margin-top: 4px;
  color: #FFFFFF;
  /* Accent deepened for a text-bearing fill: 5.3:1 with white. */
  background: var(--lp-accent-solid);
  border: 1px solid var(--lp-accent-solid);

  &:hover:not(:disabled) {
    background: var(--lp-teal);
    border-color: var(--lp-teal);
  }

  &:active:not(:disabled) {
    background: #235B61;
    border-color: #235B61;
  }
`;

const ProviderButton = styled.button`
  ${buttonBase}
  margin-bottom: 8px;
  color: var(--lp-fg);
  background: var(--lp-surface);
  border: 1px solid var(--lp-border-strong);

  /* Canonical hover for a bordered surface: the border takes the accent. */
  &:hover:not(:disabled) {
    border-color: var(--lp-accent);
    background: var(--lp-surface-sunken);
  }

  &:active:not(:disabled) {
    background: var(--lp-surface-sunken);
    border-color: var(--lp-accent-solid);
  }
`;

/* Inline spinner -- a true circle, the one place --r-round applies. */
const Spinner = styled.span`
  width: 16px;
  height: 16px;
  flex: none;
  border: 2px solid currentColor;
  border-right-color: transparent;
  border-radius: ${canonical.radius.round};
  animation: ${spin} 640ms linear infinite;

  @media (prefers-reduced-motion: reduce) {
    animation-duration: 2s;
  }
`;

const ErrorMessage = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 8px;
  margin-bottom: 16px;
  padding: 10px 12px;
  font-size: 13px;
  line-height: 1.45;
  color: var(--lp-danger);
  background: var(--lp-danger-bg);
  border: 1px solid var(--lp-danger);
  border-radius: var(--lp-r-square);

  svg {
    flex: none;
    margin-top: 1px;
  }
`;

const SecurityNote = styled.div`
  margin-top: 24px;
  padding-top: 16px;
  border-top: 1px solid var(--lp-border);
`;

const SecurityTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 6px;
  font-family: ${canonical.fontFamily.mono};
  font-size: 10.5px;
  font-weight: 600;
  letter-spacing: 1.2px;
  color: var(--lp-accent);

  &:dir(ltr) {
    text-transform: uppercase;
  }
`;

const SecurityText = styled.div`
  font-size: 12px;
  line-height: 1.6;
  color: var(--lp-fg-muted);
`;

const Footer = styled.div`
  margin-top: 24px;
  text-align: center;
  font-size: 11px;
  color: var(--lp-fg-muted);
`;

const Divider = styled.div`
  display: flex;
  align-items: center;
  margin: 22px 0;

  &::before,
  &::after {
    content: '';
    flex: 1;
    border-bottom: 1px solid var(--lp-border);
  }

  span {
    padding: 0 12px;
    font-size: 11px;
    letter-spacing: 1px;
    color: var(--lp-fg-muted);

    &:dir(ltr) {
      text-transform: uppercase;
    }
  }
`;

const DomainNote = styled.div`
  margin-top: 2px;
  text-align: center;
  font-size: 11.5px;
  line-height: 1.5;
  color: var(--lp-fg-muted);
`;

const Login: React.FC = () => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [microsoftLoading, setMicrosoftLoading] = useState(false);
  const { login, firebaseLogin } = useAuth();
  const { showNotification } = useNotification();
  // The rest of the portal has a dark mode; this screen used to ignore it and
  // render light regardless. It now reads the same context every other surface does.
  const { isDark } = useTheme();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await login(username, password);
      showNotification('Login successful', 'success');
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Invalid credentials');
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSignIn = useCallback(async () => {
    setError('');
    setGoogleLoading(true);

    try {
      console.log('Starting Firebase Google Sign-in...');

      const result = await signInWithPopup(auth, googleProvider);
      console.log('Google sign-in successful:', result.user?.email);

      if (result.user) {
        const email = result.user.email?.toLowerCase() || '';

        // Check if email is from allowed domain
        if (!isAllowedEmail(email)) {
          console.warn('User email not authorized:', email);
          await auth.signOut();
          setError('Only @silacities.com and @silainsights.com email addresses are allowed.');
          setGoogleLoading(false);
          return;
        }

        // Get Firebase ID token and send to backend
        const idToken = await result.user.getIdToken();
        await firebaseLogin(idToken);
        showNotification('Google login successful', 'success');
      }
    } catch (err: any) {
      console.error('Google sign-in error:', err);

      if (err.code === 'auth/popup-closed-by-user') {
        setError('Sign-in cancelled');
      } else if (err.code === 'auth/popup-blocked') {
        setError('Pop-up blocked. Please allow pop-ups for this site.');
      } else if (err.code === 'auth/cancelled-popup-request') {
        setError('Another sign-in request is already in progress.');
      } else if (err.code === 'auth/network-request-failed') {
        setError('Network error. Please check your connection.');
      } else if (err.code === 'auth/unauthorized-domain') {
        setError(`This domain is not authorized for Firebase Auth. Please contact the administrator.`);
      } else {
        const errorMessage = err.response?.data?.detail || err.message || 'Google authentication failed';
        setError(errorMessage);
      }
    } finally {
      setGoogleLoading(false);
    }
  }, [firebaseLogin, showNotification]);

  const handleMicrosoftSignIn = useCallback(async () => {
    setError('');
    setMicrosoftLoading(true);

    try {
      console.log('Starting Firebase Microsoft Sign-in...');

      const result = await signInWithPopup(auth, microsoftProvider);
      console.log('Microsoft sign-in successful:', result.user?.email);

      if (result.user) {
        const email = result.user.email?.toLowerCase() || '';

        // Check if email is from allowed domain
        if (!isAllowedEmail(email)) {
          console.warn('User email not authorized:', email);
          await auth.signOut();
          setError('Only @silacities.com and @silainsights.com email addresses are allowed.');
          setMicrosoftLoading(false);
          return;
        }

        // Get Firebase ID token and send to backend
        const idToken = await result.user.getIdToken();
        await firebaseLogin(idToken);
        showNotification('Microsoft login successful', 'success');
      }
    } catch (err: any) {
      console.error('Microsoft sign-in error:', err);

      if (err.code === 'auth/popup-closed-by-user') {
        setError('Sign-in cancelled');
      } else if (err.code === 'auth/popup-blocked') {
        setError('Pop-up blocked. Please allow pop-ups for this site.');
      } else if (err.code === 'auth/cancelled-popup-request') {
        setError('Another sign-in request is already in progress.');
      } else if (err.code === 'auth/network-request-failed') {
        setError('Network error. Please check your connection.');
      } else if (err.code === 'auth/unauthorized-domain') {
        setError(`This domain is not authorized for Firebase Auth. Please contact the administrator.`);
      } else {
        const errorMessage = err.response?.data?.detail || err.message || 'Microsoft authentication failed';
        setError(errorMessage);
      }
    } finally {
      setMicrosoftLoading(false);
    }
  }, [firebaseLogin, showNotification]);

  return (
    <LoginContainer $dark={isDark}>
      <LoginBox>
        <LogoContainer>
          <LogoImage src="/silacities-logo-black.png" alt="SilaCities" />
          <SystemName>GUS</SystemName>
          <AdminBadge>Admin Portal</AdminBadge>
        </LogoContainer>

        <Title>Multi-Tenant Management System</Title>
        <Subtitle>
          Manage GUS deployments, tenants, and infrastructure
        </Subtitle>

        {error && (
          <ErrorMessage role="alert" aria-live="assertive">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="12" cy="12" r="10"/>
              <line x1="12" y1="8" x2="12" y2="12"/>
              <line x1="12" y1="16" x2="12.01" y2="16"/>
            </svg>
            {error}
          </ErrorMessage>
        )}

        {/* Firebase Google Sign-In Button */}
        <ProviderButton
          type="button"
          onClick={handleGoogleSignIn}
          disabled={googleLoading || microsoftLoading || loading}
        >
          {googleLoading ? (
            <Spinner aria-hidden="true" />
          ) : (
            <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
              <path fillRule="evenodd" clipRule="evenodd" d="M17.64 9.20454C17.64 8.56636 17.5827 7.95273 17.4764 7.36364H9V10.845H13.8436C13.635 11.97 13.0009 12.9232 12.0477 13.5614V15.8196H14.9564C16.6582 14.2527 17.64 11.9455 17.64 9.20454Z" fill="#4285F4"/>
              <path fillRule="evenodd" clipRule="evenodd" d="M9 18C11.43 18 13.4673 17.1941 14.9564 15.8195L12.0477 13.5614C11.2418 14.1014 10.2109 14.4204 9 14.4204C6.65591 14.4204 4.67182 12.8373 3.96409 10.71H0.957275V13.0418C2.43818 15.9832 5.48182 18 9 18Z" fill="#34A853"/>
              <path fillRule="evenodd" clipRule="evenodd" d="M3.96409 10.71C3.78409 10.17 3.68182 9.59318 3.68182 9C3.68182 8.40682 3.78409 7.83 3.96409 7.29V4.95818H0.957273C0.347727 6.17318 0 7.54773 0 9C0 10.4523 0.347727 11.8268 0.957273 13.0418L3.96409 10.71Z" fill="#FBBC05"/>
              <path fillRule="evenodd" clipRule="evenodd" d="M9 3.57955C10.3214 3.57955 11.5077 4.03364 12.4405 4.92545L15.0218 2.34409C13.4632 0.891818 11.4259 0 9 0C5.48182 0 2.43818 2.01682 0.957275 4.95818L3.96409 7.29C4.67182 5.16273 6.65591 3.57955 9 3.57955Z" fill="#EA4335"/>
            </svg>
          )}
          {googleLoading ? 'Signing in...' : 'Continue with Google'}
        </ProviderButton>

        {/* Firebase Microsoft Sign-In Button */}
        <ProviderButton
          type="button"
          onClick={handleMicrosoftSignIn}
          disabled={googleLoading || microsoftLoading || loading}
        >
          {microsoftLoading ? (
            <Spinner aria-hidden="true" />
          ) : (
            <svg width="18" height="18" viewBox="0 0 21 21" fill="none">
              <rect x="1" y="1" width="9" height="9" fill="#F25022"/>
              <rect x="11" y="1" width="9" height="9" fill="#7FBA00"/>
              <rect x="1" y="11" width="9" height="9" fill="#00A4EF"/>
              <rect x="11" y="11" width="9" height="9" fill="#FFB900"/>
            </svg>
          )}
          {microsoftLoading ? 'Signing in...' : 'Continue with Microsoft'}
        </ProviderButton>
        <DomainNote>Only @silacities.com and @silainsights.com accounts</DomainNote>

        <Divider><span>or</span></Divider>

        <Form onSubmit={handleSubmit}>
          <InputGroup>
            <Label>Username</Label>
            <Input
              type="text"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              placeholder="Enter your username"
              required
              autoComplete="username"
            />
          </InputGroup>

          <InputGroup>
            <Label>Password</Label>
            <Input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Enter your password"
              required
              autoComplete="current-password"
            />
          </InputGroup>

          <Button type="submit" disabled={loading || googleLoading || microsoftLoading}>
            {loading ? (
              <>
                <Spinner aria-hidden="true" />
                Signing in...
              </>
            ) : (
              'Sign In with Password'
            )}
          </Button>
        </Form>

        <SecurityNote>
          <SecurityTitle>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
              <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
            </svg>
            Authorized Access Only
          </SecurityTitle>
          <SecurityText>
            This is a restricted administrative system for managing GUS deployments.
            Unauthorized access attempts are logged and monitored.
          </SecurityText>
        </SecurityNote>
      </LoginBox>

      <Footer>
        GUS Admin Portal v1.0.0 | SilaCities
      </Footer>
    </LoginContainer>
  );
};

export default Login;
